import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { parseISO, isAfter } from 'date-fns';
import {
  useAuthActions,
  useUserProducts,
  useUserCourses,
  useUserInfo,
  useAuthUser,
} from '../providers/AuthProvider';
import {
  useUserProductsLoading,
  useUserCoursesLoading,
  useUserInfoLoading,
} from '../store/useAuthStore';
import {
  updateUserProductSafe,
  removeUserProductSafe,
  updateUserCourseSafe,
} from '../services/adminManager';
import {
  updateUserInfo,
  deleteUser,
  type UserProduct,
  type UserCourse,
  type UserInfo,
  type UpdateUserPayload,
} from '../services/userManager';
import { canDeleteAdminUser } from '../config/env';

interface UseAdminUserReturn {
  userProducts: UserProduct[];
  userCourses: UserCourse[];
  userInfo: UserInfo | null;
  loadingProducts: boolean;
  loadingCourses: boolean;
  loadingUserInfo: boolean;
  deleteInProgress?: number;
  selectedUserCourse?: UserCourse;
  userId: string | undefined;
  editUserOpened: boolean;
  savingUser: boolean;
  deletingUser: boolean;
  openEditUser: () => void;
  closeEditUser: () => void;
  handleUpdateUser: (payload: UpdateUserPayload) => Promise<void>;
  confirmDeleteUser: () => void;
  openModal: (product: UserProduct) => void;
  removeProduct: (productId: number) => void;
  updateProduct: (productId: number, date: Date | string | null) => void;
  handleUpdateCourse: (values: {
    current_section: number;
    current_time: number;
    completed_at: Date | null;
    passed_at: Date | null;
  }) => void;
  setSelectedUserCourse: (course: UserCourse | undefined) => void;
  greaterThanNow: (isoDate: string) => string;
  refreshProducts: () => void;
  refreshCourses: () => void;
}

export const useAdminUser = (): UseAdminUserReturn => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authUser = useAuthUser();
  const [deleteInProgress, setDeleteInProgress] = useState<
    number | undefined
  >();
  const [selectedUserCourse, setSelectedUserCourse] = useState<
    UserCourse | undefined
  >();
  const [editUserOpened, setEditUserOpened] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);

  const { fetchUserProducts, fetchUserCourses, fetchUserInfo, clearUserData } =
    useAuthActions();
  const userProducts = useUserProducts();
  const userCourses = useUserCourses();
  const userInfo = useUserInfo();
  const loadingProducts = useUserProductsLoading();
  const loadingCourses = useUserCoursesLoading();
  const loadingUserInfo = useUserInfoLoading();

  const memoizedFetchUserInfo = useCallback(
    (userId: string) => {
      fetchUserInfo(userId);
    },
    [fetchUserInfo]
  );

  const memoizedFetchUserProducts = useCallback(
    (userId: string) => {
      fetchUserProducts(userId);
    },
    [fetchUserProducts]
  );

  const memoizedFetchUserCourses = useCallback(
    (userId: string) => {
      fetchUserCourses(userId);
    },
    [fetchUserCourses]
  );

  const memoizedClearUserData = useCallback(() => {
    clearUserData();
  }, [clearUserData]);

  useEffect(() => {
    if (id) {
      memoizedClearUserData();
    }
  }, [id, memoizedClearUserData]);

  useEffect(() => {
    if (id) {
      memoizedFetchUserInfo(id);
      memoizedFetchUserProducts(id);
      memoizedFetchUserCourses(id);
    }
  }, [
    id,
    memoizedFetchUserInfo,
    memoizedFetchUserProducts,
    memoizedFetchUserCourses,
  ]);

  const removeProduct = useCallback(
    async (productId: number) => {
      if (!id) return;

      setDeleteInProgress(productId);
      try {
        await removeUserProductSafe(id, productId);
        notifications.show({
          color: 'teal',
          withBorder: true,
          radius: 'lg',
          title: 'Product removed',
          message: 'Product has been removed successfully',
        });
        memoizedFetchUserProducts(id);
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        console.error('error', error, errorMessage);
        notifications.show({
          color: 'red',
          withBorder: true,
          radius: 'lg',
          title: 'An error occurred',
          autoClose: false,
          message: errorMessage,
        });
      } finally {
        setDeleteInProgress(undefined);
      }
    },
    [id, memoizedFetchUserProducts]
  );

  const openModal = useCallback(
    (product: UserProduct) => {
      modals.openConfirmModal({
        title: 'Please confirm your action',
        children: `Are you sure you want to remove this product? ${product.name ?? ''}`,
        labels: { confirm: 'Confirm', cancel: 'Cancel' },
        onCancel: () => {
          // Cancel handler
        },
        onConfirm: () => {
          const productId = product.product_id || product.id;
          if (productId && typeof productId === 'number') {
            removeProduct(productId);
          }
        },
      });
    },
    [removeProduct]
  );

  const updateProduct = useCallback(
    async (productId: number, date: Date | string | null) => {
      if (!id) return;

      try {
        const validUntil =
          date == null
            ? null
            : typeof date === 'string'
              ? date.slice(0, 10)
              : [
                  date.getFullYear(),
                  String(date.getMonth() + 1).padStart(2, '0'),
                  String(date.getDate()).padStart(2, '0'),
                ].join('-');
        const payload = { validUntil };
        await updateUserProductSafe(id, productId, payload);
        notifications.show({
          color: 'teal',
          withBorder: true,
          radius: 'lg',
          title: 'Product updated',
          message: 'Product validity date updated successfully',
        });
        memoizedFetchUserProducts(id);
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        console.error('error', error, errorMessage);
        notifications.show({
          color: 'red',
          withBorder: true,
          radius: 'lg',
          title: 'An error occurred',
          autoClose: false,
          message: errorMessage,
        });
      }
    },
    [id, memoizedFetchUserProducts]
  );

  const handleUpdateCourse = useCallback(
    async (values: {
      current_section: number;
      current_time: number;
      completed_at: Date | null;
      passed_at: Date | null;
    }) => {
      if (!id || !selectedUserCourse) return;

      const courseId = selectedUserCourse.courseId;
      if (!courseId) return;

      try {
        await updateUserCourseSafe(id, courseId, {
          current_section: values.current_section,
          current_time: values.current_time,
          completed_at: values.completed_at?.toISOString(),
          passed_at: values.passed_at?.toISOString(),
        });
        notifications.show({
          message: 'saved',
          title: 'Saved',
          color: 'teal',
        });
        setSelectedUserCourse(undefined);
        memoizedFetchUserCourses(id);
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        });
      }
    },
    [id, selectedUserCourse, memoizedFetchUserCourses]
  );

  const greaterThanNow = useCallback((isoDate: string) => {
    if (!isoDate) return 'default';
    const date = parseISO(isoDate);
    return isAfter(date, new Date()) ? 'teal' : 'red';
  }, []);

  const refreshProducts = useCallback(() => {
    if (id) memoizedFetchUserProducts(id);
  }, [id, memoizedFetchUserProducts]);

  const refreshCourses = useCallback(() => {
    if (id) memoizedFetchUserCourses(id);
  }, [id, memoizedFetchUserCourses]);

  const openEditUser = useCallback(() => {
    setEditUserOpened(true);
  }, []);

  const closeEditUser = useCallback(() => {
    setEditUserOpened(false);
  }, []);

  const handleUpdateUser = useCallback(
    async (payload: UpdateUserPayload) => {
      if (!id) return;

      setSavingUser(true);
      try {
        await updateUserInfo(id, payload);
        notifications.show({
          color: 'teal',
          withBorder: true,
          radius: 'lg',
          title: 'Utente aggiornato',
          message: 'Le informazioni utente sono state salvate',
        });
        setEditUserOpened(false);
        memoizedFetchUserInfo(id);
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        notifications.show({
          color: 'red',
          withBorder: true,
          radius: 'lg',
          title: 'Errore aggiornamento',
          autoClose: false,
          message: errorMessage,
        });
      } finally {
        setSavingUser(false);
      }
    },
    [id, memoizedFetchUserInfo]
  );

  const removeUser = useCallback(async () => {
    if (!id) return;
    if (!canDeleteAdminUser(authUser?.email)) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Non autorizzato',
        message: 'Non hai i permessi per eliminare utenti',
      });
      return;
    }

    setDeletingUser(true);
    try {
      await deleteUser(id);
      notifications.show({
        color: 'teal',
        withBorder: true,
        radius: 'lg',
        title: 'Utente eliminato',
        message: 'L’utente è stato eliminato definitivamente',
      });
      memoizedClearUserData();
      navigate('/admin/users');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Errore eliminazione',
        autoClose: false,
        message: errorMessage,
      });
    } finally {
      setDeletingUser(false);
    }
  }, [id, memoizedClearUserData, navigate, authUser?.email]);

  const confirmDeleteUser = useCallback(() => {
    if (!canDeleteAdminUser(authUser?.email)) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Non autorizzato',
        message: 'Non hai i permessi per eliminare utenti',
      });
      return;
    }

    const label =
      userInfo?.email ||
      userInfo?.full_name ||
      (id ? `ID ${id}` : 'questo utente');

    modals.openConfirmModal({
      title: 'Elimina utente',
      children: `Sei sicuro di voler eliminare definitivamente ${label}? Verranno rimossi anche i record collegati (prodotti/corsi assegnati, dispositivi, permessi, ecc.). I corsi e i prodotti del catalogo non vengono eliminati. L'operazione non può essere annullata.`,
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        void removeUser();
      },
    });
  }, [userInfo, id, removeUser, authUser?.email]);

  return {
    userProducts,
    userCourses,
    userInfo,
    loadingProducts,
    loadingCourses,
    loadingUserInfo,
    deleteInProgress,
    selectedUserCourse,
    userId: id,
    editUserOpened,
    savingUser,
    deletingUser,
    openEditUser,
    closeEditUser,
    handleUpdateUser,
    confirmDeleteUser,
    openModal,
    removeProduct,
    updateProduct,
    handleUpdateCourse,
    setSelectedUserCourse,
    greaterThanNow,
    refreshProducts,
    refreshCourses,
  };
};
